import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Calendar, Folder, Shield, TrendingUp,
  Lock, LockKeyhole, CloudUpload, Bell, Users, ShieldCheck,
  Star, Sun, Moon,
} from 'lucide-react';
import { useTheme } from '../context/ThemeContext';

// Landing page — 1:1 replica of the user-provided mockup (light + dark).
// Uses blue accent (#2563EB / #3B82F6) locally without touching the global
// design tokens (primary stays orange for the authenticated app).
// Hero images are served from /public/landing/ to avoid CORS/loading
// issues we saw with the S3 asset host on theme swaps.
const MOCKUP_LIGHT = '/landing/hero-light.png';
const MOCKUP_DARK = '/landing/hero-dark.png';

const NAV_LINKS = [
  { key: 'inicio', label: 'Inicio', href: '#inicio' },
  { key: 'funciones', label: 'Funciones', href: '#funciones' },
  { key: 'beneficios', label: 'Beneficios', href: '#beneficios' },
  { key: 'planes', label: 'Planes', href: '#planes' },
  { key: 'seguridad', label: 'Seguridad', href: '#seguridad' },
  { key: 'nosotros', label: 'Sobre nosotros', href: '#nosotros' },
];

const HERO_FEATURES = [
  { icon: Calendar,   title: 'Planificá', subtitle: 'tu día' },
  { icon: Folder,     title: 'Organizá',  subtitle: 'tu vida' },
  { icon: Shield,     title: 'Protegé',   subtitle: 'lo importante' },
  { icon: TrendingUp, title: 'Lográ tus', subtitle: 'objetivos' },
];

const BOTTOM_FEATURES = [
  { icon: ShieldCheck,  title: 'Privacidad y seguridad',
    desc: 'Tus datos están protegidos con tecnología de nivel bancario.' },
  { icon: LockKeyhole,  title: 'Acceso seguro',
    desc: 'Accedé desde cualquier lugar, cuando lo necesites.' },
  { icon: CloudUpload,  title: 'Sincronización total',
    desc: 'Web, móvil y desktop. Todo siempre actualizado.' },
  { icon: Bell,         title: 'Avisos inteligentes',
    desc: 'Recordatorios y alertas para que nada importante se te pase.' },
  { icon: Users,        title: 'Hecho para vos',
    desc: 'Una herramienta simple, flexible y en constante mejora.' },
];

// Static demo avatars for the social proof block. UI Faces / Unsplash-style
// portrait heads sourced from a public API so we don't need to host assets.
const AVATARS = [
  'https://i.pravatar.cc/80?img=12',
  'https://i.pravatar.cc/80?img=32',
  'https://i.pravatar.cc/80?img=47',
  'https://i.pravatar.cc/80?img=68',
  'https://i.pravatar.cc/80?img=15',
];

function NavBar({ onLogin, onRegister, theme, toggleTheme }) {
  const isDark = theme === 'dark';
  return (
    <header
      className="sticky top-0 z-40 backdrop-blur-md bg-white/70 dark:bg-[#0a0f1c]/80 border-b border-slate-200/60 dark:border-white/5"
      data-testid="landing-nav"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 h-16 sm:h-20 flex items-center gap-4 sm:gap-8">
        {/* Logo */}
        <a href="#inicio" className="flex flex-col leading-none" data-testid="landing-logo">
          <span className="font-display font-extrabold text-2xl sm:text-3xl text-slate-900 dark:text-white tracking-tight">
            V<span className="text-[#2563EB] dark:text-[#3B82F6]">&amp;</span>O
          </span>
          <span className="text-[9px] sm:text-[10px] tracking-[0.28em] font-semibold text-slate-500 dark:text-slate-400 mt-0.5">
            VIDA EN ORDEN
          </span>
        </a>

        {/* Center nav */}
        <nav className="hidden lg:flex items-center gap-7 xl:gap-10 mx-auto">
          {NAV_LINKS.map((link, idx) => {
            const active = idx === 0;
            return (
              <a
                key={link.key}
                href={link.href}
                data-testid={`nav-${link.key}`}
                className={
                  'text-sm font-medium transition-colors relative pb-1 ' +
                  (active
                    ? 'text-[#2563EB] dark:text-[#3B82F6]'
                    : 'text-slate-700 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white')
                }
              >
                {link.label}
                {active && (
                  <span className="absolute left-0 right-0 -bottom-0.5 h-[2px] bg-[#2563EB] dark:bg-[#3B82F6] rounded-full" />
                )}
              </a>
            );
          })}
        </nav>

        {/* Right actions */}
        <div className="flex items-center gap-2 sm:gap-3 ml-auto lg:ml-0">
          <button
            type="button"
            onClick={toggleTheme}
            aria-label={isDark ? 'Cambiar a modo claro' : 'Cambiar a modo oscuro'}
            data-testid="landing-theme-toggle"
            className="h-10 w-14 rounded-full bg-white dark:bg-white/10 ring-1 ring-slate-200 dark:ring-white/10 flex items-center justify-center hover:bg-slate-50 dark:hover:bg-white/20 transition-colors"
          >
            {isDark
              ? <Sun className="w-5 h-5 text-amber-400" strokeWidth={2} />
              : <Sun className="w-5 h-5 text-amber-500" strokeWidth={2} />}
          </button>
          <button
            type="button"
            onClick={onLogin}
            data-testid="landing-login-btn"
            className="hidden sm:inline-flex px-4 lg:px-5 h-10 items-center rounded-full ring-1 ring-slate-300 dark:ring-white/20 text-slate-900 dark:text-white text-sm font-semibold hover:bg-slate-50 dark:hover:bg-white/10 transition-colors"
          >
            Iniciar sesión
          </button>
          <button
            type="button"
            onClick={onRegister}
            data-testid="landing-register-btn"
            className="inline-flex px-4 lg:px-5 h-10 items-center rounded-full bg-[#2563EB] hover:bg-[#1E4FCF] dark:bg-[#3B82F6] dark:hover:bg-[#2563EB] text-white text-sm font-semibold shadow-lg shadow-[#2563EB]/25 dark:shadow-[#3B82F6]/25 transition-colors"
          >
            Comenzar ahora
          </button>
        </div>
      </div>
    </header>
  );
}

function HeroSection({ theme }) {
  const isDark = theme === 'dark';
  const heroImg = isDark ? MOCKUP_DARK : MOCKUP_LIGHT;

  return (
    <section id="inicio" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 pt-10 sm:pt-14 pb-8">
      <div className="grid lg:grid-cols-2 gap-8 lg:gap-10 items-center">
        {/* Left column — copy */}
        <div className="order-2 lg:order-1">
          <p className="text-[10px] sm:text-xs font-extrabold uppercase tracking-[0.18em] text-[#2563EB] dark:text-[#3B82F6] mb-5">
            MENOS CAOS. MÁS ENFOQUE.
          </p>
          <h1 className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-slate-900 dark:text-white mb-4">
            Organizá tu vida.
            <br />
            <span className="text-[#2563EB] dark:text-[#3B82F6]">Tomá el control.</span>
          </h1>
          <p className="text-base sm:text-lg text-slate-600 dark:text-slate-300 leading-relaxed max-w-lg mb-10">
            Tu herramienta todo en uno para planificar, organizar y simplificar lo que realmente importa.
          </p>

          {/* 4 icon features */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 sm:gap-6 mb-10 max-w-lg">
            {HERO_FEATURES.map(({ icon: Icon, title, subtitle }) => (
              <div key={title} className="flex flex-col items-start" data-testid={`hero-feat-${title.toLowerCase().replace(/\s+/g, '-')}`}>
                <div className="h-11 w-11 rounded-xl bg-[#EEF2FF] dark:bg-[#1E293B] ring-1 ring-[#DBEAFE] dark:ring-white/10 flex items-center justify-center mb-3">
                  <Icon className="w-5 h-5 text-[#2563EB] dark:text-[#3B82F6]" strokeWidth={2} />
                </div>
                <p className="text-sm font-semibold text-slate-900 dark:text-white leading-tight">
                  {title}
                </p>
                <p className="text-sm font-semibold text-slate-900 dark:text-white leading-tight">
                  {subtitle}
                </p>
              </div>
            ))}
          </div>

          {/* Social proof */}
          <div className="flex items-start gap-4">
            <div className="flex -space-x-3">
              {AVATARS.map((src, i) => (
                <img
                  key={i}
                  src={src}
                  alt=""
                  loading="lazy"
                  className="h-9 w-9 rounded-full ring-2 ring-white dark:ring-[#0a0f1c] object-cover bg-slate-200"
                />
              ))}
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-0.5 mb-1" aria-label="5 estrellas">
                {[0, 1, 2, 3, 4].map((i) => (
                  <Star key={i} className="w-3.5 h-3.5 fill-[#2563EB] text-[#2563EB] dark:fill-[#3B82F6] dark:text-[#3B82F6]" />
                ))}
              </div>
              <p className="text-sm font-semibold text-slate-900 dark:text-white">
                Profesionales que ya confían en V&amp;O
              </p>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5 leading-relaxed max-w-xs">
                Una herramienta creada para ayudarte a vivir con más orden y claridad.
              </p>
            </div>
          </div>
        </div>

        {/* Right column — hero visual (cropped to NEO + desk area only) */}
        <div className="order-1 lg:order-2 relative">
          <div
            className="relative rounded-3xl overflow-hidden ring-1 ring-slate-200 dark:ring-white/5 shadow-2xl shadow-slate-900/10 dark:shadow-black/40 bg-slate-100 dark:bg-[#0a0f1c] aspect-[4/3]"
            data-testid="hero-visual"
          >
            <img
              src={heroImg}
              alt="NEO, el asistente virtual de Vida en Orden, trabajando en su escritorio"
              className="absolute inset-0 w-full h-full select-none"
              style={{
                objectFit: 'cover',
                objectPosition: '75% 32%',
                transform: 'scale(1.75)',
                transformOrigin: '75% 32%',
              }}
              draggable={false}
            />
          </div>
        </div>
      </div>
    </section>
  );
}

function BottomFeatures() {
  return (
    <section
      id="funciones"
      className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 mt-6 sm:mt-10"
      data-testid="landing-bottom-features"
    >
      <div className="rounded-3xl bg-white dark:bg-[#0d1425] ring-1 ring-slate-200 dark:ring-white/5 shadow-lg shadow-slate-900/5 dark:shadow-black/30 px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-x-6 gap-y-6 lg:divide-x lg:divide-slate-200 dark:lg:divide-white/5">
          {BOTTOM_FEATURES.map(({ icon: Icon, title, desc }, idx) => (
            <div
              key={title}
              className={idx > 0 ? 'lg:pl-6' : ''}
              data-testid={`bottom-feat-${title.toLowerCase().replace(/\s+/g, '-').replace(/[^\w-]/g, '')}`}
            >
              <div className="flex items-start gap-3">
                <div className="h-10 w-10 rounded-xl bg-[#EEF2FF] dark:bg-[#1E293B] ring-1 ring-[#DBEAFE] dark:ring-white/10 flex items-center justify-center flex-shrink-0">
                  <Icon className="w-5 h-5 text-[#2563EB] dark:text-[#3B82F6]" strokeWidth={2} />
                </div>
                <div className="min-w-0">
                  <p className="font-semibold text-slate-900 dark:text-white text-sm sm:text-base mb-1">
                    {title}
                  </p>
                  <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 leading-relaxed">
                    {desc}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-8 sm:py-10 mt-4">
      <div className="flex items-center justify-center gap-2 text-slate-500 dark:text-slate-400">
        <Lock className="w-4 h-4" strokeWidth={2} />
        <p className="text-sm">
          Confiable. Simple. Eficiente. Así es <span className="font-semibold text-slate-700 dark:text-slate-200">Vida en Orden</span>.
        </p>
      </div>
    </footer>
  );
}

export default function Landing() {
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();

  // Smooth scroll for in-page anchor navigation
  useEffect(() => {
    const handler = (e) => {
      const link = e.target.closest('a[href^="#"]');
      if (!link) return;
      const id = link.getAttribute('href').slice(1);
      if (!id) return;
      const target = document.getElementById(id);
      if (target) {
        e.preventDefault();
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    };
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, []);

  return (
    <div
      className="min-h-screen bg-white dark:bg-[#050912] text-slate-900 dark:text-white"
      data-testid="landing-page"
    >
      <NavBar
        onLogin={() => navigate('/login')}
        onRegister={() => navigate('/register')}
        theme={theme}
        toggleTheme={toggleTheme}
      />
      <HeroSection theme={theme} />
      <BottomFeatures />
      <Footer />
    </div>
  );
}
